import { Flex } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import ProductsList from "../components/ProductList/ProductsList";
import FiltersDesktop from "../components/Filters/FiltersDesktop";
import FiltersMobile from "../components/Filters/mobile/FiltersMobile";
import { memo } from "react";
import InfoOnProductsPage from "../components/InfoOnProductsPage/InfoOnProductsPage";
import ItemInfoDesktop from "../components/ItemInfo/ItemInfoDesktop";
import RefreshPopup from "../components/RefreshPopup/RefreshPopup";
import { useAtom } from "jotai";

import ProductGroupList from "../components/ProductList/ProductGroupList";
import ListingsList from "../components/ProductList/ListingsList";
import VendorBanner from "../components/VendorBanner/VendorBanner";
import { allCategoryInfoAtom } from "../atom/calibers";
import { categoriesPageModeAtom } from "../atom/listings";

const ProductsPage = ({ slider = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customHTML, setCustomHTML] = useState(null);

  const [allCalibers] = useAtom(allCategoryInfoAtom);
  const [chooseCaliber, setChooseCaliber] = useState(null);
  const [newQuery, setNewQuery] = useState(null);



  const currentCaliber = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    // For SEO URLs: /pd/{weapon_category}-ammo/{caliber}/{brand}
    // pathSegments[0] = "pd", pathSegments[1] = weapon_category, pathSegments[2] = caliber
    if (pathSegments.length >= 3 && (pathSegments[0] === "ls" || pathSegments[0] === "pd" || pathSegments[0] === "brands")) {
      return pathSegments[2] || null;
    }

    // For URLs without prefix (like /handgun-ammo/{caliber})
    if (pathSegments.length >= 2 && !["ls", "pd", "brands"].includes(pathSegments[0])) {
      return pathSegments[1] || null;
    }

    // For legacy URLs or category-only URLs
    return pathSegments[1] || null;
  }, [location.pathname]);

  useEffect(() => {
    if (allCalibers.length > 0 && currentCaliber) {
      const matchedCaliber = allCalibers
        .flatMap((item) => item.calibers)
        .find((caliberItem) => {
          return caliberItem.seo_caliber === currentCaliber;
        });

      if (matchedCaliber) {
        setChooseCaliber(matchedCaliber.caliber);
      }
    }
    // eslint-disable-next-line
  }, [allCalibers, currentCaliber]);

  useEffect(() => {
    const updatedState = `${location.pathname}${location.search}`;
    let finalQuery = updatedState;

    if (chooseCaliber && !location.search.includes("caliber=")) {
      const encodedCaliber = encodeURIComponent(chooseCaliber);
      finalQuery = updatedState.includes("?")
        ? `${updatedState}&caliber=${encodedCaliber}`
        : `${updatedState}?caliber=${encodedCaliber}`;
    }

    if (slider) {
      const productIdMatch = location.pathname.match(/-(\d+)(?:\?|$)/);
      const productId = productIdMatch ? productIdMatch[1] : null;

      if (productId) {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set("pgId", productId);

        if (!queryParams.has("page")) queryParams.set("page", "1");
        if (!queryParams.has("per_page")) queryParams.set("per_page", "12");
        if (!queryParams.has("sort_by")) {
          queryParams.set("sort_by", "landed_cpr");
        }

        queryParams.delete("period");
        finalQuery = `product/${productId}?${queryParams.toString()}`;
      }
    }

    setNewQuery(finalQuery);
  }, [chooseCaliber, slider, location.pathname, location.search]);

  const [categoriesPageMode, setCategoriesPageMode] = useAtom(categoriesPageModeAtom);

  const isListingsMode = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    // Check if this is a SEO URL
    if (firstSegment === "ls" || firstSegment === "pd" || firstSegment === "brands") {
      const isListings = firstSegment === "ls" || firstSegment === "brands";
      return isListings;
    }

    // For URLs without prefix (like /handgun-ammo), default to listings mode
    // This will be handled by SeoRedirectHandler, but we need to show filters
    return true; // Default to listings mode for category URLs
  }, [location.pathname]);

  // Sync categoriesPageModeAtom with URL
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (firstSegment === "ls" || firstSegment === "pd" || firstSegment === "brands") {
      const shouldBeListings = firstSegment === "ls" || firstSegment === "brands";
      const expectedMode = shouldBeListings ? "listings" : "products";

      if (categoriesPageMode !== expectedMode) {
        setCategoriesPageMode(expectedMode);
      }
    } else if (!slider) {
      navigate('/not-found');
    }
  }, [location.pathname, categoriesPageMode, setCategoriesPageMode, navigate, slider]);


  return (
    <>
      <RefreshPopup />
      <Flex
        maxW={"1240px"}
        w={"100%"}
        mx={"auto"}
        pt={{ base: "22px", lg: "45px" }}
        flexDirection={"column"}
        mb={{ base: "72px", md: "64px" }}
        px={{ base: "22px", lg: "10px" }}
      >
        {(location.pathname.includes("aguila") ||
          location.search.includes("algulia=true")) && (
            <Flex justifyContent={"center"} w={"100%"} mt={"10px"} mb={"20px"}>
              <VendorBanner />
            </Flex>
          )}

        <InfoOnProductsPage
          slider={slider}
          locationState={newQuery}
          setCustomHTML={setCustomHTML}
          caliber={currentCaliber}
        />
        <Flex justifyContent={"space-between"} w={"100%"} gap={"20px"}>
          {slider ? <ItemInfoDesktop /> : <FiltersDesktop />}

          {slider ? (
            <ProductGroupList
              slider={slider}
              locationState={newQuery}
              customHTML={customHTML}
            />
          ) : isListingsMode ? (
            <ListingsList locationState={newQuery} customHTML={customHTML} />
          ) : (
            <ProductsList locationState={newQuery} customHTML={customHTML} />
          )}
        </Flex>
        {!slider && <FiltersMobile />}
      </Flex>
    </>
  );
};

export default memo(ProductsPage);
